import React, { useState, useEffect, useRef } from 'react';
import InfoCard from '../components/InfoCard';
import {
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaFileCsv,
  FaFlag,
  FaSearch,
  FaSlidersH,
  FaStar,
  FaSyncAlt,
  FaTimes,
  FaUserSlash
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ContactTable } from '../components/ContactTable';
import { callDetailUrl, toCsv } from '../common/csv';
import {
  // getEntities,
  // languages as getLanguages,
  search,
  listCampaignsApi,
  listTemplates,
  getAgents,
  getAssignedTo,
  getJobs
} from '../api/api';
import { Select } from '../components/Select';
import { MultiSelect } from '../components/MultiSelect';
import LoadingState, { InlineSpinner } from '../components/LoadingState';
import { qaScoreOf } from '../common/assessment';
import { devLog } from '../common/devLog';

// Tailwind class names must appear as literal strings somewhere in source for
// the JIT scanner to pick them up — hence a lookup table instead of building
// e.g. `bg-${color}-50` at runtime, which the scanner can't see.
//
// Dark-safe tinted icon tiles (50/100 bg + 600/700 text) per the CloudZA
// playbook, mapped to shortcut semantics. Note the brand remaps in
// tailwind.config.js: 'cloudza' resolves to the violet ramp and 'coral' to
// the teal accent, so the pairs below stay visually distinct.
const QUICK_SEARCH_TILES = {
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  green: 'bg-green-50 text-green-700',
  coral: 'bg-coral-50 text-coral-700',
  gray: 'bg-gray-100 text-gray-500',
  cloudza: 'bg-cloudza-50 text-cloudza-700',
  purple: 'bg-purple-50 text-purple-700',
};

// Quick search templates. Each `query` is a function so time-relative values
// ("Today", "This Week") are computed when the shortcut is clicked, not once
// at module load.
const QUICK_SEARCHES = [
  {
    name: "Failed Compliance Today",
    icon: <FaExclamationTriangle />,
    color: 'red',
    query: () => ({
      timestampFrom: new Date().setHours(0, 0, 0, 0).toString(),
      compliance: "false"
    })
  },
  {
    name: "Failed Conduct Today",
    icon: <FaFlag />,
    color: 'amber',
    query: () => ({
      timestampFrom: new Date().setHours(0, 0, 0, 0).toString(),
      conduct: "false"
    })
  },
  {
    name: "High Scores (90+)",
    icon: <FaStar />,
    color: 'green',
    query: () => ({
      qaScoreMin: "90",
      qaScoreMax: "100"
    })
  },
  {
    name: "Assessed Calls",
    icon: <FaCheckCircle />,
    color: 'coral',
    query: () => ({
      assessed: "true"
    })
  },
  {
    name: "Unassigned Calls",
    icon: <FaUserSlash />,
    color: 'gray',
    query: () => ({
      assignedTo: "admin"
    })
  },
  {
    name: "Long Calls (>10 min)",
    icon: <FaClock />,
    color: 'cloudza',
    query: () => ({
      durationMin: "600"
    })
  },
  {
    name: "Processing Calls",
    icon: <FaSyncAlt />,
    color: 'cloudza',
    query: () => ({
      status: "InProgress"
    })
  },
  {
    name: "Uploaded This Week",
    icon: <FaCalendarAlt />,
    color: 'purple',
    query: () => ({
      timestampFrom: getStartOfWeek().toString()
    })
  }
];

const Search = ({ setAlert }) => {
  const [activeTab, setActiveTab] = useState('quick'); // quick, basic, advanced
  const [query, setQuery] = useState({});
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Load more: the server returns one page, newest first, and `before` asks for
  // the next. Load more repeats the search that produced the page, not whatever
  // the form holds now, and a newer search drops any page still in flight.
  const [cursor, setCursor] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchRun = useRef(0);

  // Filter options state
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [jobs, setJobs] = useState([]);

  // Existing SWR hooks
  // const { data: entities, error: entitiesError } = useSWR('/entities', getEntities);
  // const { data: languageCodes, error: languagesError } = useSWR('/languages', getLanguages);

  // Load filter options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Use existing APIs
        const campaignsRes = await listCampaignsApi();
        setCampaigns(campaignsRes.campaigns || []);

        const templatesRes = await listTemplates();
        const templateNames = (templatesRes.templates || []).map(t => t.templateName);
        setTemplates(templateNames);
        setFilteredTemplates(templateNames);

        // Get agents using dedicated API
        const agentsRes = await getAgents();
        setAgents(Array.isArray(agentsRes) ? agentsRes : []);

        // Get assignedTo using dedicated API
        const assignedToRes = await getAssignedTo();
        setAssignedToOptions(Array.isArray(assignedToRes) ? assignedToRes : []);

        // Get jobs using dedicated API
        const jobsRes = await getJobs();
        setJobs(Array.isArray(jobsRes) ? jobsRes : []);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadOptions();
  }, []);

  // Filter templates by selected campaign
  useEffect(() => {
    if (query.customer) {
      const campaign = campaigns.find(c => c.campaignName === query.customer);
      if (campaign && campaign.templates) {
        const campaignTemplates = templates.filter(t =>
          campaign.templates.includes(t)
        );
        setFilteredTemplates(campaignTemplates);
      } else {
        setFilteredTemplates(templates);
      }
    } else {
      setFilteredTemplates(templates);
    }
  }, [query.customer, campaigns, templates]);

  const updateQuery = (value, field) => {
    devLog('updateQuery called with field:', field, 'value:', value);
    setQuery(prev => {
      const updated = { ...prev, [field]: value };
      devLog('Updated query before cleanup:', updated);
      Object.keys(updated).forEach(k => {
        const v = updated[k];
        if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
          delete updated[k];
        }
      });
      devLog('Updated query after cleanup:', updated);
      return updated;
    });
  };

  const loadQuickSearch = (quickSearch) => {
    const quickQuery = typeof quickSearch.query === 'function'
      ? quickSearch.query()
      : quickSearch.query;
    setQuery(quickQuery);
    fetchResults(quickQuery);
  };

  const fetchResults = async (params = {}) => {
    const run = searchRun.current + 1;
    searchRun.current = run;
    const firstPage = { ...params, count: window.pcaSettings?.api?.pageSize || 25 };
    setIsSearching(true);
    setIsLoadingMore(false); // a Load more for the old search no longer counts
    setCursor(null);
    try {
      const data = await search(firstPage);
      devLog('data:', data);
      if (run !== searchRun.current) return;

      const records = Array.isArray(data) ? data : (data.Records || data.Items || data.items || []);

      setResults(records);
      setLastParams(firstPage);
      setCursor(Array.isArray(data) ? null : data.before || null);
      setHasSearched(true);
      devLog(records)
    } catch (err) {
      console.error(err);
      if (run !== searchRun.current) return;
      // A 404 from the backend means "nothing matched", not "the search broke"
      // — render the normal empty state instead of an error toast.
      if (err.status === 404) {
        setResults([]);
        setHasSearched(true);
      } else {
        setAlert && setAlert({
          variant: 'error',
          heading: 'Search Error',
          text: 'Failed to perform search.'
        });
      }
    } finally {
      // Only the newest search may clear the spinner; a stale one would drop it early.
      if (run === searchRun.current) setIsSearching(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || isLoadingMore) return;
    const run = searchRun.current;
    setIsLoadingMore(true);
    try {
      const data = await search({ ...lastParams, before: cursor });
      if (run !== searchRun.current) return;
      setResults(prev => [...prev, ...(data.Records || [])]);
      setCursor(data.before || null);
    } catch (err) {
      console.error('Failed to load more search results', err);
      if (run !== searchRun.current) return;
      setAlert && setAlert({
        variant: 'error',
        heading: 'Search Error',
        text: 'Failed to load more calls.'
      });
    } finally {
      // A stale page clearing this would re-enable the button while a newer
      // request is still out, and a second click would append its rows twice.
      if (run === searchRun.current) setIsLoadingMore(false);
    }
  };

  const clearAll = () => {
    searchRun.current += 1;
    setIsSearching(false);
    setIsLoadingMore(false);
    setQuery({});
    setResults([]);
    setCursor(null);
    setLastParams(null);
    setHasSearched(false);
  };

  

  const performSearch = async () => {
    await fetchResults(query);
  };

  const exportResults = (format) => {
    if (format === 'csv') {
      const headers = ['Timestamp', 'Job Name', 'Agent', 'Campaign', 'Template', 'QA Score', 'Compliance', 'Conduct', 'Duration', 'GUID', 'Conversation ID', 'DNIS', 'Link'];
      const rows = results.map(item => [
        // Guard: a missing/invalid timestamp would make toISOString() throw
        (() => {
          const d = new Date(item.timestamp);
          return item.timestamp && !isNaN(d.getTime()) ? d.toISOString() : '';
        })(),
        item.jobName || '',
        item.agent || '',
        item.customer || '',
        item.template || '',
        qaScoreOf(item) ?? 'N/A',
        // Absent means nothing was assessed - not a fail.
        item.Result?.Compliance === false ? 'Fail' : item.Result?.Compliance === true ? 'Pass' : 'N/A',
        // Absent means nothing was assessed - not a pass.
        item.Result?.Conduct === false ? 'Fail' : item.Result?.Conduct === true ? 'Pass' : 'N/A',
        item.duration || '',
        item.guid || '',
        item.conversationId || '',
        item.dnis || '',
        callDetailUrl(item)
      ]);
      const csv = toCsv(headers, rows);

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(results, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const formatFilterLabel = (key) => {
    const labels = {
      agent: 'Agent',
      customer: 'Campaign',
      template: 'Template',
      qaScoreMin: 'Min Score',
      qaScoreMax: 'Max Score',
      compliance: 'Compliance',
      conduct: 'Agent Conduct',
      compliancePriority: 'Compliance Priority',
      assessed: 'Assessment Status',
      status: 'Status',
      timestampFrom: 'From',
      timestampTo: 'To',
      jobId: 'Job ID',
      assignedTo: 'Assigned To',
      durationMin: 'Min Duration',
      durationMax: 'Max Duration',
      language: 'Language',
      entity: 'Entities',
      jobName: 'Job Name',
      msisdn: 'MSISDN',
      conversationId: 'Conversation ID',
      dnis: 'DNIS'
    };
    return labels[key] || key;
  };

  const formatFilterValue = (key, value) => {
    if (key === 'timestampFrom' || key === 'timestampTo') {
      return new Date(parseInt(value)).toLocaleDateString();
    }
    if (key === 'compliance') {
      return value === 'true' ? 'Compliant' : 'Non-Compliant';
    }
    if (key === 'conduct') {
      return value === 'true' ? 'Conduct Pass' : 'Conduct Fail';
    }
    if (key === 'assessed') {
      return value === 'true' ? 'Assessed' : 'Not Assessed';
    }
    if (key === 'compliancePriority') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const removeAgentFromFilter = (agentToRemove) => {
    if (Array.isArray(query.agent)) {
      const updatedAgents = query.agent.filter(a => a !== agentToRemove);
      updateQuery(updatedAgents.length > 0 ? updatedAgents : null, 'agent');
    } else {
      updateQuery(null, 'agent');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Multi-Layered Search</h1>
        <p className="text-gray-500 text-sm mt-1">Find calls across campaigns using indexed and refined filters</p>
        {/* Was a native <details>, the only one in the app — now the same
            collapsible info card used everywhere else. Long reference content,
            so it starts closed. */}
        <InfoCard
          id="search-filter-behaviour"
          title="How search filters behave"
          defaultOpen={false}
          className="mt-3"
        >
          <div>
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-gray-800 mb-1 text-xs uppercase tracking-wider">Database-Level Filters (Fast)</p>
                <p className="text-xs text-gray-400 mb-1">These pull optimized records directly from indexed data:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Agent, Campaign, Template, Language, Entity</li>
                  <li>Assigned To, Assessment Status, Compliance</li>
                  <li>Date Range (Timestamp), QA Score Range</li>
                  <li>Sentiment filters</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-1 text-xs uppercase tracking-wider">Post-Processing Filters (Applied After Database Pull)</p>
                <p className="text-xs text-gray-400 mb-1">These refine results from database queries:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Exact QA Score Range (refines bucket results)</li>
                  <li>Duration Range, Status</li>
                  <li>GUID, Job ID, Job Name</li>
                  <li>Confidence Minimum</li>
                </ul>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="font-semibold text-gray-800 mb-1 text-xs uppercase tracking-wider">Filter Logic</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li><strong>Multiple Agents or QA Score Ranges:</strong> OR logic (returns any match)</li>
                  <li><strong>All Other Filters:</strong> AND logic (results must match all criteria)</li>
                </ul>
              </div>
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-8 min-w-max" data-tour="search-tabs">
          <button
            onClick={() => setActiveTab('quick')}
            className={`${
              activeTab === 'quick'
                ? 'border-cloudza-600 text-cloudza-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            } inline-flex items-center gap-2 whitespace-nowrap py-2.5 px-1 border-b-2 font-semibold text-sm transition-colors`}
          >
            <FaBolt className="text-[13px]" />
            Quick Filters
          </button>
          <button
            onClick={() => setActiveTab('basic')}
            className={`${
              activeTab === 'basic'
                ? 'border-cloudza-600 text-cloudza-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            } inline-flex items-center gap-2 whitespace-nowrap py-2.5 px-1 border-b-2 font-semibold text-sm transition-colors`}
          >
            <FaSearch className="text-[13px]" />
            Basic Search
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`${
              activeTab === 'advanced'
                ? 'border-cloudza-600 text-cloudza-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            } inline-flex items-center gap-2 whitespace-nowrap py-2.5 px-1 border-b-2 font-semibold text-sm transition-colors`}
          >
            <FaSlidersH className="text-[13px]" />
            Advanced
          </button>
        </nav>
      </div>

      {/* Quick Filters Tab */}
      {activeTab === 'quick' && (
        <div className="card space-y-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Quick Search Shortcuts</h3>
            <p className="text-xs text-gray-500 mt-0.5">One click, common filters already applied</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            {QUICK_SEARCHES.map(qs => (
              <button
                key={qs.name}
                onClick={() => loadQuickSearch(qs)}
                className="group flex items-center gap-3.5 p-4 border border-gray-200 rounded-xl hover:border-cloudza-300 hover:bg-cloudza-50/40 text-left transition-colors"
              >
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${QUICK_SEARCH_TILES[qs.color]}`}>
                  {qs.icon}
                </span>
                <span className="flex-1 min-w-0 text-[13px] font-semibold text-gray-800 leading-snug">{qs.name}</span>
                <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Basic Search Tab */}
      {activeTab === 'basic' && (
        <div className="card space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Agent</label>
              <MultiSelect
                options={agents.map(a => ({ value: a, label: a }))}
                value={query.agent && Array.isArray(query.agent) ? query.agent : []}
                onChange={values => {
                  devLog('MultiSelect onChange received:', values);
                  updateQuery(values && values.length > 0 ? values : null, 'agent');
                }}
                placeholder="Select agents..."
              />
            </div>

            {/* Conversation ID Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Conversation ID</label>
              <input
                type="text"
                value={query.conversationId || ''}
                onChange={e => updateQuery(e.target.value, 'conversationId')}
                placeholder="Enter Conversation ID..."
                className="input-field"
              />
            </div>

            {/* Campaign Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign</label>
              <Select
                options={campaigns.map(c => ({
                  value: c.campaignName,
                  label: c.campaignName
                }))}
                value={query.customer ? { value: query.customer, label: query.customer } : null}
                onChange={opt => {
                  updateQuery(opt?.value ?? null, 'customer');
                  // Clear template whenever the campaign changes or is cleared —
                  // the old campaign's template doesn't apply to the new one.
                  updateQuery(null, 'template');
                }}
                placeholder="Select campaign..."
                isClearable
              />
            </div>

            {/* Template Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Template</label>
              <Select
                options={filteredTemplates.map(t => ({ value: t, label: t }))}
                value={query.template ? { value: query.template, label: query.template } : null}
                onChange={opt => updateQuery(opt?.value ?? null, 'template')}
                placeholder="Select template..."
                isClearable
                isDisabled={!query.customer}
              />
              {!query.customer && (
                <p className="text-xs text-gray-500 mt-1">Select a campaign first</p>
              )}
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Date Range</label>
              <div className="relative">
                <DatePicker
                  selectsRange
                  startDate={query.timestampFrom ? new Date(parseInt(query.timestampFrom)) : null}
                  endDate={query.timestampTo ? new Date(parseInt(query.timestampTo)) : null}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    if (start) {
                      updateQuery(start.getTime(), 'timestampFrom');
                    } else {
                      updateQuery(null, 'timestampFrom');
                    }
                    if (end) {
                      updateQuery(new Date(end).setHours(23, 59, 59, 999), 'timestampTo');
                    } else {
                      updateQuery(null, 'timestampTo');
                    }
                  }}
                  maxDate={new Date()}
                  className="input-field w-full"
                  placeholderText="Select date range"
                  isClearable
                  dateFormat="MMM dd, yyyy"
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            {/* DNIS Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">DNIS</label>
              <input
                type="text"
                value={query.dnis || ''}
                onChange={e => updateQuery(e.target.value, 'dnis')}
                placeholder="Enter DNIS..."
                className="input-field"
              />
            </div>

            {/* QA Score Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">QA Score Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Min"
                  value={query.qaScoreMin || ''}
                  onChange={e => updateQuery(e.target.value, 'qaScoreMin')}
                  className="input-field"
                />
                <span className="text-sm text-gray-500">to</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Max"
                  value={query.qaScoreMax || ''}
                  onChange={e => updateQuery(e.target.value, 'qaScoreMax')}
                  className="input-field"
                />
              </div>
            </div>

            {/* Compliance Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Compliance</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'true', label: 'Compliant Only' },
                  { value: 'false', label: 'Non-Compliant Only' }
                ]}
                value={query.compliance ?
                  { value: query.compliance, label: query.compliance === 'true' ? 'Compliant Only' : 'Non-Compliant Only' }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'compliance')}
              />
            </div>

            {/* Agent Behavior and Conduct Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Agent Conduct</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'true', label: 'Conduct Pass' },
                  { value: 'false', label: 'Conduct Fail' }
                ]}
                value={query.conduct ?
                  { value: query.conduct, label: query.conduct === 'true' ? 'Conduct Pass' : 'Conduct Fail' }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'conduct')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Calls scored before conduct assessment was enabled are not indexed for this filter.
              </p>
            </div>

            {/* Compliance Priority Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Compliance Priority</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' }
                ]}
                value={query.compliancePriority ?
                  { value: query.compliancePriority, label: formatFilterValue('compliancePriority', query.compliancePriority) }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'compliancePriority')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Priority filtering only applies to calls processed on or after this filter's release. Calls processed earlier have no priority data and won't match this filter.
              </p>
            </div>
          </div>

          <div className="flex space-x-2">
            <button className="btn-primary disabled:opacity-50" onClick={performSearch} disabled={isSearching}>
              {isSearching ? <InlineSpinner light /> : <FaSearch />}
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            <button className="btn-outline" onClick={clearAll}>
              <FaTimes />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Advanced Search Tab */}
      {activeTab === 'advanced' && (
        <div className="card space-y-6">
          {/* All filters from Basic + Additional advanced filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Job ID */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Job ID</label>
              <Select
                options={jobs.map(j => ({ value: j, label: j }))}
                value={query.jobId ? { value: query.jobId, label: query.jobId } : null}
                onChange={opt => updateQuery(opt?.value ?? null, 'jobId')}
                placeholder="Select job..."
                isClearable
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Assigned To</label>
              <Select
                options={assignedToOptions.map(a => ({ value: a, label: a }))}
                value={query.assignedTo ? { value: query.assignedTo, label: query.assignedTo } : null}
                onChange={opt => updateQuery(opt?.value ?? null, 'assignedTo')}
                placeholder="Select QA analyst..."
                isClearable
              />
            </div>

            {/* GUID */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">GUID</label>
              <input
                type="text"
                value={query.guid || ''}
                onChange={e => updateQuery(e.target.value, 'guid')}
                placeholder="Enter GUID..."
                className="input-field"
              />
            </div>

            {/* Assessment Status Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Assessment Status</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'true', label: 'Assessed Only' },
                  { value: 'false', label: 'Not Assessed' }
                ]}
                value={query.assessed ?
                  { value: query.assessed, label: query.assessed === 'true' ? 'Assessed Only' : 'Not Assessed' }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'assessed')}
              />
            </div>

            {/* Status
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Status</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'Done', label: 'Completed' },
                  { value: 'InProgress', label: 'Processing' },
                  { value: 'Failed', label: 'Failed' }
                ]}
                value={query.status ?
                  { value: query.status, label: query.status }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'status')}
              />
            </div> */}

            {/* Duration Range */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Duration (seconds)</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={query.durationMin || ''}
                  onChange={e => updateQuery(e.target.value, 'durationMin')}
                  className="input-field"
                />
                <span className="text-sm text-gray-500">to</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={query.durationMax || ''}
                  onChange={e => updateQuery(e.target.value, 'durationMax')}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            <button className="btn-primary disabled:opacity-50" onClick={performSearch} disabled={isSearching}>
              {isSearching ? <InlineSpinner light /> : <FaSearch />}
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            <button className="btn-outline" onClick={clearAll}>
              <FaTimes />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {Object.keys(query).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg">
          <span className="text-sm font-semibold text-gray-800">Active Filters:</span>
          {Object.entries(query).flatMap(([key, value]) => {
            // Handle agent array separately to show individual chips
            if (key === 'agent' && Array.isArray(value)) {
              return value.filter(agent => agent != null).map(agent => (
                <span key={`agent-${agent}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-cloudza-50 text-cloudza-700 border-cloudza-100">
                  Agent: {agent}
                  <button
                    onClick={() => removeAgentFromFilter(agent)}
                    className="ml-1.5 opacity-60 hover:opacity-100 transition-opacity"
                    aria-label={`Remove filter Agent: ${agent}`}
                  >
                    <FaTimes className="text-[10px]" />
                  </button>
                </span>
              ));
            }
            // Regular filters
            return (
              <span key={key} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-cloudza-50 text-cloudza-700 border-cloudza-100">
                {formatFilterLabel(key)}: {formatFilterValue(key, value)}
                <button
                  onClick={() => updateQuery(null, key)}
                  className="ml-1.5 opacity-60 hover:opacity-100 transition-opacity"
                  aria-label={`Remove filter ${formatFilterLabel(key)}`}
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </span>
            );
          })}
          <button
            onClick={clearAll}
            className="text-xs text-red-700 font-semibold hover:underline"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Results Table */}
      {/* Fresh search with nothing on screen yet — without this the page sits
          blank until the response lands and looks like the button did nothing. */}
      {isSearching && results.length === 0 && (
        <div className="card">
          <div className="flex items-center gap-2.5 mb-4">
            <InlineSpinner />
            <h2 className="text-sm font-bold text-gray-900">Searching…</h2>
          </div>
          <LoadingState variant="rows" header={false} label="Loading search results" />
        </div>
      )}
      {hasSearched && results.length === 0 && !isSearching && (
        <div className="card">
          <NoMatches />
        </div>
      )}
      {results.length > 0 && (
        <ContactTable
          data={results}
          loading={isSearching}
          empty={<NoMatches />}
          header={
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Count intentionally omitted here — ContactTable's header slot
                  already renders the canonical "N matches" count beside this. */}
              <div>
                <h2 className="text-sm font-bold text-gray-900">
                  Search Results
                </h2>
                {cursor && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Newest first. More calls match: the count, filters and exports cover the calls loaded so far.
                  </p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => exportResults('csv')}
                  className="btn-outline !py-1.5 !px-3.5 !text-xs"
                >
                  <FaFileCsv />
                  Export CSV
                </button>
                <button
                  onClick={() => exportResults('json')}
                  className="btn-outline !py-1.5 !px-3.5 !text-xs"
                >
                  <FaDownload />
                  Export JSON
                </button>
              </div>
            </div>
          }
          onRowClick={item => window.open(`/dashboard/${item.key || item.filename}`, '_blank', 'noopener,noreferrer')}
          onLoadMore={loadMore}
          hasMore={!!cursor}
          isLoadingMore={isLoadingMore}
        />
      )}
    </div>
  );
};

const NoMatches = () => (
  <div className="text-center py-12">
    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
    <p className="mt-1 text-sm text-gray-500">Try different filters or clear some criteria.</p>
  </div>
);

// Helper function
function getStartOfWeek() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek;
  return new Date(now.setDate(diff)).setHours(0, 0, 0, 0);
}

export default Search;